import React, {
  useContext,
  useState,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import { isMobile } from "react-device-detect";
import { MachinesContext } from "../../../context/MachinesContext";
import { GlobalContext } from "../../../context/GlobalContext";
import { AOO_API_HOST } from "../../../env";
import MobileOptions from "../../Mobile/MobileOptions";
import CoverUploader from "../CoverUploader/CoverUploader";
import Dialog from "../Alert/Dialog";
import DaysToFix from "../Alert/DayToFix";
import { postAction } from "../WidgetActions";
import { ContextMenuItem, submenuBaseStyles } from "./ContextMenu";
import EventMenuItem from "./EventMenuItem";
import Mark from "./Mark";
import SubMenu from "./SubMenu";
import {
  useWorkerItems,
  useInspectorItems,
  useReasonItems,
  useFixItems,
  useCoverItems,
} from "./MenuItems";
import {
  getSubmenuPosition,
  getOptimalSubmenuPosition,
  type SpaceInfo,
} from "./ContextMenuUtils";
import { useGetSubmenuRef } from "./GetSubmenuRef";

export default function ContextMenuComponent() {
  const {
    menuRef,
    setRoot,
    setsubStatusRoot,
    setsubWorkerRoot,
    setsubReasonRoot,
    setsubFixRoot,
    setsubInspectorRoot,
    getSubmenuRef,
  } = useGetSubmenuRef();

  // Menu position state
  const [menuPosition, setMenuPosition] = useState({ x: 0, y: 0 });

  // State for active submenu
  const [activeSubmenu, setActiveSubmenu] = useState<string | null>(null);
  const [isSendAlertPromptVisible, setIsSendAlertPromptVisible] =
    useState(false);
  const [sendAlarmHoldProgress, setSendAlarmHoldProgress] = useState(0);
  const [sendAlarmHoldTimer, setSendAlarmHoldTimer] = useState<NodeJS.Timeout | null>(null);
  const sendAlarmHoldingRef = useRef(false);
  // State to hold calculated submenu styles
  const [submenuStyles, setSubmenuStyles] = useState<
    Record<string, React.CSSProperties>
  >({});
  // State for long press tracking
  const [touchTimer, setTouchTimer] = useState<NodeJS.Timeout | null>(null);
  const [touching, setTouching] = useState(false);

  const [isCoverPictureModalOpen, setIsCoverPictureModalOpen] = useState(false);
  const [isDaysToFixModalOpen, setIsDaysToFixModalOpen] = useState(false);
  const [isRemoveDaysToFixModalOpen, setIsRemoveDaysToFixModalOpen] =
    useState(false);

  const {
    machine,
    globalStatus,
    globalPoints,
    show,
    setShow,
    isActive: isMobileOptionsActive,
    setLog,
    isSlackViewActive,
    setEventOption,
    workers,
    failureReasons,
  } = useContext(GlobalContext);
  const { machineDtoMap } = useContext(MachinesContext);

  // Close menu and clear states (Moved before handleToggleInspector)
  const closeMenu = useCallback(() => {
    setShow(false);
    setActiveSubmenu(null); // Reset active submenu on close
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "auto";
  }, [setShow]);

  const updateInspectors = useCallback(
    async (idsToUpdate: string[]) => {
      if (!machine?.Id) return; // Don't update if no machine context

      try {
        await postAction(
          AOO_API_HOST + `/aoo-api/Productions/post-machine-inspector`,
          {
            MachinesId: [machine.Id],
            WorkerIds: idsToUpdate,
          }
        );
      } catch (error) {
        console.error("Failed to update inspectors:", error);
      }
    },
    [machine?.Id]
  ); // Removed postAction and AOO_API_HOST as they are constants/imports

  // Function to handle toggling an inspector selection
  const handleToggleInspector = useCallback(
    (workerName: string) => {
      if (!machine?.Id || !workers) return;

      // Find the worker by name
      const worker = workers.find((w) => w.FullName === workerName);
      if (!worker) return;

      // Check if this worker is already an inspector for the machine
      const machineInspectors =
        machine?.InspectorsId ||
        machineDtoMap?.[machine?.Id]?.InspectorsId ||
        [];
      const isInspector = machineInspectors.some((insp) => {
        if (typeof insp === "object") {
          const inspName =
            insp.FullName || insp.fullName || insp.Name || insp.name;
          return inspName === workerName;
        }
        return false;
      });

      // Create array of worker IDs for the API call
      let idsToUpdate = [];

      if (isInspector) {
        // If already an inspector, remove this worker from inspectors
        idsToUpdate = machineInspectors
          .filter((insp) => {
            if (typeof insp === "object") {
              const inspName =
                insp.FullName || insp.fullName || insp.Name || insp.name;
              return inspName !== workerName;
            }
            return true;
          })
          .map((insp) => {
            if (typeof insp === "object") {
              return insp.WorkerId || insp.workerId || insp.Id || insp.id;
            }
            return insp;
          })
          .filter((id) => id != null);
      } else {
        // If not an inspector, add this worker to inspectors
        const existingIds = machineInspectors
          .map((insp) => {
            if (typeof insp === "object") {
              return insp.WorkerId || insp.workerId || insp.Id || insp.id;
            }
            return insp;
          })
          .filter((id) => id != null);

        idsToUpdate = [...existingIds, worker.WorkerId];
      }

      // Update inspectors via API
      updateInspectors(idsToUpdate);
      closeMenu();
    },
    [machine, machineDtoMap, workers, updateInspectors, closeMenu]
  );

  // Function to handle toggling worker selection
  const handleToggleWorker = useCallback(
    (workerName: string) => {
      // Find the worker details from the name
      const worker = workers?.find((w) => w.FullName === workerName);
      if (!worker || !machine?.Id) return;

      // Check if this worker is already assigned to the machine
      const isWorkerAlreadyAssigned =
        machine.Workers &&
        Array.isArray(machine.Workers) &&
        machine.Workers.some((w) => {
          // Try different property patterns for worker identification
          if (typeof w === "object") {
            const wName = w.FullName || w.FullName || w.Initials;
            return wName === workerName;
          }
          return false;
        });

      console.log(
        "Toggle worker:",
        workerName,
        "Is already assigned:",
        isWorkerAlreadyAssigned
      );

      // If selecting the same worker that's already assigned, remove it
      if (isWorkerAlreadyAssigned) {
        console.log("Removing worker");
        // Remove worker
        postAction(
          AOO_API_HOST + `/aoo-api/Productions/remove-worker`,
          machine.Id
        );
      } else {
        console.log("Changing worker to:", workerName);
        // Always post with only one worker ID to ensure we're replacing any existing worker
        // This enforces the "one worker per machine" rule
        postAction(AOO_API_HOST + `/aoo-api/Productions/post-new-flag`, {
          MachinesId: [machine.Id],
          WorkerIds: [worker.WorkerId],
          WorkerType: "W",
        });
      }

      closeMenu();
    },
    [workers, machine, closeMenu]
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // If the send-alarm dialog is visible, don't close the menu from outside clicks/touches
      if (isSendAlertPromptVisible) return;

      if (
        menuRef.current &&
        event.target instanceof Node &&
        !menuRef.current.contains(event.target)
      ) {
        // Check if the click is inside any open submenu before closing
        const activeSubmenuRef = activeSubmenu
          ? getSubmenuRef(activeSubmenu)
          : null;
        if (
          !activeSubmenuRef ||
          (event.target instanceof Node &&
            !activeSubmenuRef.contains(event.target))
        ) {
          setShow(false);
          setActiveSubmenu(null);
        }
      }
    };

    const handleTouchOutside = (event: TouchEvent) => {
      // If the send-alarm dialog is visible, don't close the menu from outside clicks/touches
      if (isSendAlertPromptVisible) return;

      if (
        menuRef.current &&
        event.target instanceof Node &&
        !menuRef.current.contains(event.target)
      ) {
        const activeSubmenuRef = activeSubmenu
          ? getSubmenuRef(activeSubmenu)
          : null;
        if (
          !activeSubmenuRef ||
          (event.target instanceof Node &&
            !activeSubmenuRef.contains(event.target))
        ) {
          setShow(false);
          setActiveSubmenu(null);
          if (touchTimer) {
            clearTimeout(touchTimer);
            setTouchTimer(null);
          }
          setTouching(false);
        }
      }
    };

    document.addEventListener("click", handleClickOutside, true); // Use capture phase
    document.addEventListener("touchstart", handleTouchOutside, true); // Use capture phase

    return () => {
      document.removeEventListener("click", handleClickOutside, true);
      document.removeEventListener("touchstart", handleTouchOutside, true);
    };
  }, [setShow, touchTimer, activeSubmenu, getSubmenuRef, menuRef, isSendAlertPromptVisible]);

  // Function for handling touch start
  const handleTouchStart = useCallback(
    (submenuType: string) => (e: React.TouchEvent) => {
      // e.preventDefault();
      // e.stopPropagation(); // Let it propagate for outside detection

      // Start timer for long press detection
      const timer = setTimeout(() => {
        setActiveSubmenu(submenuType);
        setTouching(false); // Indicate long press finished
      }, 300); // 300ms for long press

      setTouchTimer(timer);
      setTouching(true); // Indicate touch has started
    },
    []
  );

  // Function for handling touch end
  const handleTouchEnd = useCallback(
    (submenuType: string) => (e: React.TouchEvent) => {
      e.preventDefault(); // Prevent default touch behavior
      // e.stopPropagation(); // Let it propagate

      // Clear timer if touch ends before long press duration
      if (touchTimer) {
        clearTimeout(touchTimer);
        setTouchTimer(null);
      }

      // If it was a short touch (touching is true and timer was cleared)
      if (touching) {
        // Toggle the submenu: open if closed/different, close if already open
        setActiveSubmenu((current) =>
          current === submenuType ? null : submenuType
        );
        setTouching(false); // Reset touching state
      }
      // If it was a long press, touching would be false already, state set by timer
    },
    [touchTimer, touching]
  ); // Dependencies: activeSubmenu is read via setter function

  // Function for handling touch cancel
  const handleTouchCancel = useCallback(() => {
    if (touchTimer) {
      clearTimeout(touchTimer);
      setTouchTimer(null);
    }
    setTouching(false);
  }, [touchTimer]);

  // React approach for toggling submenu on click (primarily for desktop)
  const toggleSubmenu = useCallback(
    (submenuType: string) => (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent click from closing the menu via outside click handler
      setActiveSubmenu((current) =>
        current === submenuType ? null : submenuType
      );
    },
    []
  );

  // Mouse hover handlers for desktop
  const handleMouseEnter = useCallback(
    (submenuType: string) => (e: React.MouseEvent) => {
      if (!isMobile) {
        setActiveSubmenu(submenuType);
      }
    },
    []
  );

  const handleMouseLeave = useCallback(
    (submenuType: string) => (e: React.MouseEvent) => {
      if (!isMobile) {
        const relatedTarget = e.relatedTarget as Node | null;
        if (!relatedTarget) {
          // If the mouse leaves the browser window or no related target, close the submenu
          setActiveSubmenu(null);
          return;
        }

        const submenuEl = getSubmenuRef(submenuType);
        const mainMenuList = menuRef.current?.querySelector("ul");

        // Check if the mouse is moving within the menu or submenu
        const isMovingWithinMenu =
          submenuEl?.contains(relatedTarget) ||
          (mainMenuList?.contains(relatedTarget) &&
            relatedTarget !== e.currentTarget);

        if (!isMovingWithinMenu) {
          // Close the submenu if the mouse is moving outside the menu
          setActiveSubmenu(null);
        }
      }
    },
    [menuRef, setActiveSubmenu, getSubmenuRef]
  );

  const handleRemoveDisagree = () => {
    setIsRemoveDaysToFixModalOpen(false);
  };
  const handleRemoveAgree = () => {
    setIsRemoveDaysToFixModalOpen(false);
    postAction(
      AOO_API_HOST + `/aoo-api/Productions/remove-fix-days`,
      machine.EventId
    );
  };

  // Calculate initial menu position (example, adjust as needed)
  useEffect(() => {
    const calculatePosition = () => {
      // Example: position based on globalPoints or another trigger event
      if (globalPoints && show) {
        const menuWidth = 200; // Approximate menu width
        const menuHeight = 300; // Approximate menu height
        const { innerWidth, innerHeight } = window;

        let x = Number(globalPoints.x);
        let y = Number(globalPoints.y);

        // Adjust if menu overflows window boundaries
        if (x + menuWidth > innerWidth) {
          x = innerWidth - menuWidth - 10; // Adjust with padding
        }
        if (y + menuHeight > innerHeight) {
          y = innerHeight - menuHeight - 10; // Adjust with padding
        }
        x = Math.max(10, x); // Ensure minimum padding from left
        y = Math.max(10, y); // Ensure minimum padding from top

        setMenuPosition({ x, y });
      }
    };

    calculatePosition();

    // Optional: Recalculate on window resize
    window.addEventListener("resize", calculatePosition);
    return () => window.removeEventListener("resize", calculatePosition);
  }, [globalPoints, show]);

  // Function to handle touch events for submenus (prevent propagation)
  const handleSubmenuTouch = useCallback((e: React.TouchEvent) => {
    e.stopPropagation();
  }, []);

  // Function to prevent page scrolling when user reaches the end of submenu scroll
  const handleSubmenuScroll = useCallback((e: React.WheelEvent) => {
    const { currentTarget } = e;
    const isAtTop = currentTarget.scrollTop === 0 && e.deltaY < 0;
    const isAtBottom =
      currentTarget.scrollHeight - currentTarget.scrollTop <=
        currentTarget.clientHeight + 1 && // +1 for tolerance
      e.deltaY > 0;

    if (isAtTop || isAtBottom) {
      // e.preventDefault();
    }
    e.stopPropagation(); // Prevent wheel event from propagating further
  }, []);

  const sendAlarm = useCallback((machineId: string) => {
    console.log("Sending alarm for machine:", machineId);
    postAction(AOO_API_HOST + `/aoo-api/Productions/send-alarm`, machineId);
  }, []);

  const displaySendAlarmPrompt = useCallback(() => {
    console.log("Showing alarm confirmation dialog");
    sendAlarmHoldingRef.current = false;
    setSendAlarmHoldProgress(0);
    if (sendAlarmHoldTimer) {
      clearInterval(sendAlarmHoldTimer);
      setSendAlarmHoldTimer(null);
    }
    setIsSendAlertPromptVisible(true);
  }, [setIsSendAlertPromptVisible, sendAlarmHoldTimer]);

  const handleSendAlarmMouseDown = useCallback(
    (e?: React.MouseEvent | React.TouchEvent) => {
      // Prevent the overlay from receiving the event (mobile generates click after touch)
      if (e && "preventDefault" in e) {
        try {
          e.preventDefault();
        } catch (err) {}
      }
      if (e && "stopPropagation" in e) {
        try {
          (e as any).stopPropagation();
        } catch (err) {}
      }

      console.log("Hold started - waiting for 3 seconds");
      sendAlarmHoldingRef.current = true;

      let progress = 0;
      let buttonStillPressed = true;

      const interval = setInterval(() => {
        // Only increment if still holding the button down
        if (!sendAlarmHoldingRef.current || !buttonStillPressed) {
          clearInterval(interval);
          setSendAlarmHoldTimer(null);
          return;
        }

        progress += 3.33; // Increments to reach 100 in ~3 seconds
        setSendAlarmHoldProgress(Math.min(progress, 100));

        if (progress >= 100) {
          clearInterval(interval);
          setSendAlarmHoldTimer(null);
          buttonStillPressed = false;
          console.log("3 seconds completed - sending alarm");
          sendAlarm(machine.Id);
          setIsSendAlertPromptVisible(false);
          closeMenu();
        }
      }, 30); // Update every 30ms for smooth progress

      setSendAlarmHoldTimer(interval as any);

      // Track if button is still pressed using global mouse/touch events
      const handleGlobalMouseUp = (ev?: Event) => {
        buttonStillPressed = false;
        sendAlarmHoldingRef.current = false;
        document.removeEventListener("mouseup", handleGlobalMouseUp);
        document.removeEventListener("touchend", handleGlobalTouchEnd);
      };

      const handleGlobalTouchEnd = (ev?: Event) => {
        buttonStillPressed = false;
        sendAlarmHoldingRef.current = false;
        document.removeEventListener("mouseup", handleGlobalMouseUp);
        document.removeEventListener("touchend", handleGlobalTouchEnd);
      };

      document.addEventListener("mouseup", handleGlobalMouseUp);
      document.addEventListener("touchend", handleGlobalTouchEnd);
    },
    [machine.Id, sendAlarm, closeMenu]
  );

  const handleSendAlarmMouseUp = useCallback(
    (e?: React.MouseEvent | React.TouchEvent | React.PointerEvent) => {
      if (e && "preventDefault" in e) {
        try {
          e.preventDefault();
        } catch (err) {}
      }
      if (e && "stopPropagation" in e) {
        try {
          (e as any).stopPropagation();
        } catch (err) {}
      }

      console.log("Hold cancelled - progress:", sendAlarmHoldProgress);
      sendAlarmHoldingRef.current = false;
      if (sendAlarmHoldTimer) {
        clearInterval(sendAlarmHoldTimer);
        setSendAlarmHoldTimer(null);
      }
      setSendAlarmHoldProgress(0);
    },
    [sendAlarmHoldTimer, sendAlarmHoldProgress]
  );

  const handleConfirmAlarm = useCallback(() => {
    console.log("Alarm confirmed");
    sendAlarm(machine.Id);
    setIsSendAlertPromptVisible(false);
    closeMenu();
  }, [machine.Id, sendAlarm, closeMenu]);

  const handleCancelAlarm = useCallback(() => {
    console.log("Alarm cancelled");
    sendAlarmHoldingRef.current = false;
    setSendAlarmHoldProgress(0);
    if (sendAlarmHoldTimer) {
      clearInterval(sendAlarmHoldTimer);
      setSendAlarmHoldTimer(null);
    }
    setIsSendAlertPromptVisible(false);
  }, [sendAlarmHoldTimer]);

  const getSubmenuStyle = useCallback(
    (submenuType: string | null): React.CSSProperties => {
      if (!submenuType || !menuRef.current) return {};

      const rootRect = menuRef.current.getBoundingClientRect();
      const submenuRef = getSubmenuRef(submenuType);
      const size = isMobile ? "mobile" : "desktop";

      // Calculate available space around the menu
      const spaceInfo: SpaceInfo = {
        spaceRight: window.innerWidth - rootRect.right,
        spaceLeft: rootRect.left,
        spaceBelow: window.innerHeight - rootRect.top,
        spaceAbove: rootRect.bottom,
        windowWidth: window.innerWidth,
        windowHeight: window.innerHeight,
      };

      const position = getOptimalSubmenuPosition(
        rootRect,
        spaceInfo,
        submenuRef,
        size
      );

      const positionStyle = getSubmenuPosition(
        position,
        rootRect,
        spaceInfo,
        size
      );

      const baseStyles = isMobile
        ? submenuBaseStyles.mobile
        : submenuBaseStyles.desktop;

      return {
        ...positionStyle,
        ...baseStyles,
      } as React.CSSProperties;
    },
    [getSubmenuRef, menuRef]
  );

  // Effect to calculate and update submenu styles when activeSubmenu changes
  useLayoutEffect(() => {
    if (activeSubmenu) {
      const style = getSubmenuStyle(activeSubmenu);
      setSubmenuStyles((prevStyles) => ({
        ...prevStyles, // Keep styles for potentially cached but inactive menus if needed
        [activeSubmenu]: style,
      }));
    } else {
      // Optionally clear styles when no submenu is active
      // setSubmenuStyles({});
    }
  }, [activeSubmenu, getSubmenuStyle]); // Depend on activeSubmenu and the style calculation function

  const changeEventStatus = async (option: any) => {
    try {
      await postAction(
        `${AOO_API_HOST}/aoo-api/Productions/post-status-urgency`,
        {
          MachineId: machine.Id,
          Status: option.color,
          IsUrgent: option.isUrgent,
        }
      );
      closeMenu(); // Close the menu after action
    } catch (error) {
      console.error(error);
      closeMenu(); // Also close on error
    }
  };

  // Common props for all submenu components
  const submenuProps = {
    onSubmenuTouch: handleSubmenuTouch,
    onSubmenuScroll: handleSubmenuScroll,
    onTouchCancel: handleTouchCancel, // Pass touch cancel handler
  };

  // Generate menu items for worker submenu
  const workerItems = useWorkerItems(
    workers,
    machine?.Id,
    machine?.Workers || [],
    handleToggleWorker,
    closeMenu
  );
  // Generate menu items for inspector submenu using machine inspectors directly
  const inspectorItems = useInspectorItems(
    workers,
    machine?.Id,
    machine?.InspectorsId || machineDtoMap?.[machine?.Id]?.InspectorsId || [],
    handleToggleInspector
  );

  // Generate menu items for reason submenu
  const reasonItems = useReasonItems(
    failureReasons,
    machine?.EventId,
    machine?.EventState,
    closeMenu
  );

  // Generate menu items for fix days submenu
  const fixItems = useFixItems(
    machine?.DaysToFix != null,
    () => setIsDaysToFixModalOpen(true),
    () => setIsRemoveDaysToFixModalOpen(true),
    closeMenu
  );

  // Generate menu items for cover picture submenu
  const coverItems = useCoverItems(
    machine?.Cover != null,
    machine?.Id,
    () => setIsCoverPictureModalOpen(true),
    closeMenu
  );

  const sendAlarmItemRender = useCallback(() => {
    return (
      <span>Send Alarm</span>
    );
  }, []);

  useEffect(() => {
    if (!show) {
      setIsSendAlertPromptVisible(false);
    }
  }, [show]);

  // Guard against rendering when machine data is not yet available
  if (!machine || !show) {
    return null;
  }

  return (
    <>
      {isMobileOptionsActive && (
        <MobileOptions
          id={machine.Id}
          status={globalStatus}
          machine={machine}
          setInput={setIsDaysToFixModalOpen}
          setRemove={setIsRemoveDaysToFixModalOpen}
          setCover={setIsCoverPictureModalOpen}
        />
      )}
      {isCoverPictureModalOpen && (
        <CoverUploader
          setCover={setIsCoverPictureModalOpen}
          remove={isCoverPictureModalOpen} // Prop name might need adjustment based on CoverUploader
        />
      )}

      <DaysToFix
        input={isDaysToFixModalOpen}
        handleClose={() => setIsDaysToFixModalOpen(false)}
        machine={machine}
      />
      <Dialog
        machine={machine} // Pass necessary machine data
        remove={isRemoveDaysToFixModalOpen}
        handleDialogDisagree={handleRemoveDisagree}
        handleRemoveAgree={handleRemoveAgree} // Changed prop name for clarity
      />

      {/* Send Alarm Confirmation Dialog */}
      {isSendAlertPromptVisible && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
          onClick={(e: React.MouseEvent) => {
            // Only close when clicking the backdrop itself, not when interacting with inner content
            if (e.target === e.currentTarget) {
              handleCancelAlarm();
            }
          }}
        >
            <div
              onTouchStart={(e) => {
                e.stopPropagation();
              }}
              onTouchEnd={(e) => {
                e.stopPropagation();
              }}
              style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              boxShadow: "0 4px 20px rgba(0, 0, 0, 0.3)",
              maxWidth: "400px",
              width: "90%",
              zIndex: 10001,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: "0 0 16px 0", color: "#333" }}>Send Alarm</h2>
            <p style={{ margin: "0 0 24px 0", color: "#666", fontSize: "14px" }}>
              Hold the button for 3 seconds to send the alarm for machine <strong>{machine?.Name || machine?.MachineCode}</strong>
            </p>

            {/* Hold to Confirm Button */}
            <button
              onMouseDown={handleSendAlarmMouseDown}
              onMouseUp={handleSendAlarmMouseUp}
              onMouseLeave={handleSendAlarmMouseUp}
              onTouchStart={handleSendAlarmMouseDown}
              onTouchEnd={handleSendAlarmMouseUp}
              onTouchCancel={handleSendAlarmMouseUp}
              onPointerDown={handleSendAlarmMouseDown}
              onPointerUp={handleSendAlarmMouseUp}
              onPointerCancel={handleSendAlarmMouseUp}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              style={{
                touchAction: "none",
                width: "100%",
                padding: "12px 16px",
                marginBottom: "12px",
                backgroundColor: sendAlarmHoldProgress > 0 ? `rgba(220, 53, 69, ${0.3 + sendAlarmHoldProgress / 100 * 0.7})` : "#e74c3c",
                color: "white",
                border: "none",
                borderRadius: "4px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
                position: "relative",
                overflow: "hidden",
                transition: "background-color 0.1s",
              }}
              aria-disabled={sendAlarmHoldProgress > 0}
            >
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  height: "100%",
                  width: `${sendAlarmHoldProgress}%`,
                  backgroundColor: "rgba(0, 0, 0, 0.2)",
                  transition: "width 0.03s linear",
                  borderRadius: "4px",
                }}
              />
              <span style={{ position: "relative", zIndex: 1, display: "block" }}>
                {sendAlarmHoldProgress > 0 ? `Holding... ${Math.round(sendAlarmHoldProgress / 100 * 3 * 10) / 10}s` : "Hold for 3 seconds"}
              </span>
            </button>

            {/* Cancel Button */}
            <button
              onClick={handleCancelAlarm}
              style={{
                width: "100%",
                padding: "12px 16px",
                backgroundColor: "#f8f9fa",
                color: "#333",
                border: "1px solid #dee2e6",
                borderRadius: "4px",
                fontSize: "16px",
                fontWeight: "600",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#e9ecef")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#f8f9fa")}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {show ? (
        <ContextMenuItem
          ref={(ref) => {
            setRoot(ref);
            menuRef.current = ref;
          }} 
          style={{
            position: "absolute",
            left: `${menuPosition.x}px`,
            top: `${menuPosition.y}px`,
            maxHeight: isMobile ? "80vh" : "auto", // Consider moving maxHeight logic to base styles
            overflowY: isMobile ? "auto" : "visible", // Consider moving overflow logic
          }}
        >
          <ul>
            <Mark
              machine={machine}
              setEventOption={setEventOption}
              color={globalStatus.color}
            />
            <EventMenuItem
              setsubStatusRoot={setsubStatusRoot} // Prop for setting the ref
              onOptionClick={changeEventStatus}
              activeSubmenu={activeSubmenu}
              onToggle={toggleSubmenu}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
              onTouchCancel={handleTouchCancel}
              submenuStyle={submenuStyles["status"] || {}} // Use calculated style from state
              onSubmenuTouch={handleSubmenuTouch}
              onSubmenuScroll={handleSubmenuScroll}
              onMouseEnter={handleMouseEnter("status")}
              onMouseLeave={handleMouseLeave("status")}
            />
            {machine.Workers && machine.Workers.length > 0 ? (
              <SubMenu
                items={workerItems}
                type="worker"
                title="Worker"
                isActive={activeSubmenu === "worker"}
                rootRef={(ref) => setsubWorkerRoot(ref)}
                style={submenuStyles["worker"] || {}}
                onToggle={toggleSubmenu("worker")}
                onTouchStart={handleTouchStart("worker")}
                onTouchEnd={handleTouchEnd("worker")}
                onMouseEnter={handleMouseEnter("worker")}
                onMouseLeave={handleMouseLeave("worker")}
                {...submenuProps}
              />
            ) : (
              <SubMenu
                items={workerItems}
                type="worker"
                title="Worker"
                isActive={activeSubmenu === "worker"}
                rootRef={(ref) => setsubWorkerRoot(ref)}
                style={submenuStyles["worker"] || {}}
                onToggle={toggleSubmenu("worker")}
                onTouchStart={handleTouchStart("worker")}
                onTouchEnd={handleTouchEnd("worker")}
                onMouseEnter={handleMouseEnter("worker")}
                onMouseLeave={handleMouseLeave("worker")}
                {...submenuProps}
              />
            )}

            {/* Inspector Submenu */}
            <SubMenu
              items={inspectorItems}
              type="inspector"
              title="Notify me"
              isActive={activeSubmenu === "inspector"}
              rootRef={(ref) => setsubInspectorRoot(ref)}
              style={submenuStyles["inspector"] || {}} 
              onToggle={toggleSubmenu("inspector")}
              onTouchStart={handleTouchStart("inspector")}
              onTouchEnd={handleTouchEnd("inspector")}
              onMouseEnter={handleMouseEnter("inspector")}
              onMouseLeave={handleMouseLeave("inspector")}
              {...submenuProps} // Pass common props
            />

            {/* Reason Submenu */}
            <SubMenu
              items={reasonItems}
              type="reason"
              title="Assign an action"
              isActive={activeSubmenu === "reason"}
              rootRef={(ref) => setsubReasonRoot(ref)}
              style={submenuStyles["reason"] || {}} // Use calculated style from state
              onToggle={toggleSubmenu("reason")}
              onTouchStart={handleTouchStart("reason")}
              onTouchEnd={handleTouchEnd("reason")}
              onMouseEnter={handleMouseEnter("reason")}
              onMouseLeave={handleMouseLeave("reason")}
              {...submenuProps} // Pass common props
            />

            {/* Fix Days Submenu */}
            <SubMenu
              items={fixItems}
              type="fix"
              title="Days to fix"
              isActive={activeSubmenu === "fix"}
              rootRef={(ref) => setsubFixRoot(ref)}
              style={submenuStyles["fix"] || {}} 
              onToggle={toggleSubmenu("fix")}
              onTouchStart={handleTouchStart("fix")}
              onTouchEnd={handleTouchEnd("fix")}
              onMouseEnter={handleMouseEnter("fix")}
              onMouseLeave={handleMouseLeave("fix")}
              {...submenuProps} // Pass common props
            />

            {/* Cover Picture Submenu */}
            <SubMenu
              items={coverItems}
              type="cover"
              title="Cover picture"
              isActive={activeSubmenu === "cover"}
              rootRef={(ref) => setsubFixRoot(ref)} 
              style={submenuStyles["cover"] || {}} 
              onToggle={toggleSubmenu("cover")}
              onTouchStart={handleTouchStart("cover")}
              onTouchEnd={handleTouchEnd("cover")}
              onMouseEnter={handleMouseEnter("cover")}
              onMouseLeave={handleMouseLeave("cover")}
              {...submenuProps} // Pass common props
            />

            {!isSlackViewActive && (
              <li
                className={"menu-item log"}
                onClick={() => {
                  setLog(true);
                  closeMenu();
                }}
              >
                Log
              </li>
            )}
            {
              <li
                className={"menu-item sendAlarm"}
                onClick={(e) => {
                  e.stopPropagation();
                  displaySendAlarmPrompt();
                }}
              >
                {sendAlarmItemRender()}
              </li>
            }
          </ul>
        </ContextMenuItem>
      ) : null}
    </>
  );
}
